import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { useAuth } from '../contexts/AuthContext';
import { usePincode } from '../contexts/PincodeContext';
import { t, getCategoryName } from '../lib/translations';
import ProductCard from '../components/ProductCard';
import Pagination from '../components/Pagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  const { language } = useAuth();
  const { currentPincode } = usePincode();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Sync state when URL params change (e.g. new search from navbar)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlCategory = searchParams.get('category') || 'all';
    setSearchQuery(urlSearch);
    setSelectedCategory(urlCategory);
    setCurrentPage(1); // reset to page 1 on new search
  }, [searchParams]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API}/products?pincode=${currentPincode}&page=${currentPage}&page_size=20`;
        
        if (selectedCategory && selectedCategory !== 'all') {
          url += `&category_id=${selectedCategory}`;
        }
        
        if (searchQuery) {
          url += `&search=${encodeURIComponent(searchQuery)}`;
        }
        
        const response = await axios.get(url);
        const data = response.data;
        setProducts(data.products || data);
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || 0);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [currentPincode, selectedCategory, searchQuery, currentPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setCurrentPage(1);
    setMobileFilterOpen(false); // auto close sheet on mobile
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set('category', value);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setCurrentPage(1);
    setMobileFilterOpen(false); // auto close sheet on mobile
    setSearchParams({});
  };

  const selectedCategoryName = categories.find(c => c.id === selectedCategory);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="products-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="container-app py-4">
          {/* Breadcrumb */}
          <div className="text-sm text-slate-500 mb-4">
            <Link to="/" className="hover:text-green-600">{t('home', language)}</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-900">{t('allProducts', language)}</span>
            {selectedCategoryName && (
              <>
                <span className="mx-2">/</span>
                <span className="text-slate-900">{getCategoryName(selectedCategoryName, language)}</span>
              </>
            )}
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
            {/* Desktop Category Filter */}
            <div className="hidden md:flex items-center gap-2">
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-48" data-testid="category-filter">
                  <SelectValue placeholder={t('categories', language)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('allProducts', language)}</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {getCategoryName(category, language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedCategory !== 'all' && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  {language === 'hi' ? 'साफ़ करें' : 'Clear'}
                </Button>
              )}
            </div>
            
            {/* Mobile Filter */}
            <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="md:hidden">
                  <Filter className="w-4 h-4 mr-2" />
                  {t('categories', language)}
                  {selectedCategory !== 'all' && (
                    <span className="ml-2 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      1
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[70vh] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>{t('categories', language)}</SheetTitle>
                </SheetHeader>
                <div className="py-4 space-y-2 overflow-y-auto max-h-[calc(70vh-80px)]">
                  <button
                    onClick={() => handleCategoryChange('all')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      selectedCategory === 'all'
                        ? 'bg-green-100 text-green-700 font-medium'
                        : 'hover:bg-slate-100'
                    }`}
                  >
                    {t('allProducts', language)}
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-green-100 text-green-700 font-medium'
                          : 'hover:bg-slate-100'
                      }`}
                    >
                      {getCategoryName(category, language)}
                    </button>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container-app py-6">
        {loading ? (
          <div className="product-grid">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden">
                <div className="aspect-square bg-slate-200 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-slate-200 rounded animate-pulse" />
                  <div className="h-4 bg-slate-200 rounded w-1/2 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Filter className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {language === 'hi' ? 'कोई उत्पाद नहीं मिला' : 'No products found'}
            </h3>
            <p className="text-slate-500 mb-4">
              {language === 'hi'
                ? 'अपनी खोज बदलने या फ़िल्टर साफ़ करने का प्रयास करें।'
                : 'Try changing your search or clearing filters.'}
            </p>
            <Button onClick={clearFilters} variant="outline">
              {language === 'hi' ? 'फ़िल्टर साफ़ करें' : 'Clear Filters'}
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4">
              {total} {language === 'hi' ? 'उत्पाद मिले' : 'products found'}
            </p>
            <div className="product-grid" data-testid="products-grid">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              language={language}
            />
          </>
        )}
      </div>
    </div>
  );
}
